<template>
    <v-col v-bind="DASHBOARD_WIDGET_COLUMNS.SMALL">
        <v-card>
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ widget.body?.title }}</template>
                <template #append>
                    <v-tooltip :text="t('common.dashboard.tooltips.export')" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                color="green"
                                icon="mdi-file-excel"
                                size="small"
                                variant="text"
                                @click="generateReport"
                            />
                        </template>
                    </v-tooltip>
                </template>
            </v-toolbar>
            <v-divider />
            <v-card-text>
                <OrdersProductsPerDayNotSentListDialog
                    :date="currentDate"
                    :is-open="dialogOpen"
                    :orders-products-per-day-not-sent="ordersProducts"
                    :partner="partner"
                    @after-leave-not-sent-orders-products-dialog="onDialogAfterLeave"
                    @close-not-sent-orders-products-dialog="closeDialog"
                />

                <v-row class="pb-1">
                    <v-col class="py-0" cols="12">
                        <v-btn
                            color="blue-grey"
                            :disabled="disableBackward"
                            icon="mdi-chevron-left"
                            variant="text"
                            @click="previousDay"
                        />
                        <span class="text-subtitle-2">{{ currentDateLabel }}</span>
                        <v-btn
                            color="blue-grey"
                            :disabled="disableForward"
                            icon="mdi-chevron-right"
                            variant="text"
                            @click="nextDay"
                        />
                    </v-col>
                </v-row>

                <v-data-table
                    :headers="headers"
                    :items="orderedOrders"
                    density="compact"
                    hide-default-footer
                    item-value="orderLocation"
                >
                    <template #item.details="{ item }">
                        <v-tooltip v-if="item.orderLocation" :text="t('common.dashboard.tooltips.showDetails')" location="top">
                            <template #activator="{ props: tooltipProps }">
                                <v-btn
                                    v-bind="tooltipProps"
                                    color="blue"
                                    icon="mdi-ballot"
                                    size="small"
                                    variant="text"
                                    @click="showDetails(item)"
                                />
                            </template>
                        </v-tooltip>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { addDays, format, isSameDay, subDays } from 'date-fns'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorHandler } from '@/composables/errorHandler'
import { useDashboardStore, useUserStore } from '@/stores'
import type { DashboardPermission, DashboardWidget } from '@/stores/dashboard'
import type { DataTableHeader } from '@/types/dataTable'
import { DASHBOARD_WIDGET_COLUMNS, getOrdersLocationSortRank } from '@/views/Dashboard/dashboardUtils'

import OrdersProductsPerDayNotSentListDialog from './OrdersProductsPerDayNotSentListDialog.vue'

type OrderLocationRow = {
    orderLocation?: string | null
    checkDate?: string | null
    toSend?: unknown
}

type NotSentDialogProductRow = {
    prodName?: string
    toSend?: unknown
    quantity?: unknown
    dueDate?: string
    partner?: string
    productId?: string | number
}

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        permissions?: DashboardPermission[]
    }>(),
    { permissions: () => [] }
)

// 3. Constants
const headers: DataTableHeader[] = [
    { title: 'Nazwa', key: 'orderLocation', sortable: false },
    { title: 'Ilość', key: 'toSend', sortable: false },
    { title: 'Szczegóły', key: 'details', sortable: false, align: 'end' }
]
const MIN_DAY_STEP = 1
const MAX_DAY_STEP = 7

// 4. Stores & top-level services
const dashboardStore = useDashboardStore()
const userStore = useUserStore()
const { t } = useI18n()

// 5. Reactive state (refs/reactive)
const currentDate = ref(new Date())
const bodyContent = props.widget?.body?.content
const content = ref<OrderLocationRow[]>(Array.isArray(bodyContent) ? (bodyContent as OrderLocationRow[]) : [])
const dayStep = ref(MIN_DAY_STEP)
const dialogOpen = ref(false)
const ordersProducts = ref<NotSentDialogProductRow[]>([])
const partner = ref('')

// 7. Computed
const currentDateLabel = computed(() => format(currentDate.value, 'dd.MM.yyyy'))
const disableForward = computed(() => dayStep.value === MAX_DAY_STEP)
const disableBackward = computed(() => dayStep.value === MIN_DAY_STEP)
const orderedOrders = computed(() => {
    const day = currentDate.value
    const rows = (content.value || []).filter((row: OrderLocationRow) => {
        if (row.checkDate == null || row.checkDate === '') return false
        try {
            return isSameDay(new Date(row.checkDate), day)
        } catch {
            return false
        }
    })
    return [...rows].sort((a, b) => getOrdersLocationSortRank(a?.orderLocation) - getOrdersLocationSortRank(b?.orderLocation))
})

// 8. Handlers / methods
function previousDay(): void {
    dayStep.value -= 1
    currentDate.value = subDays(currentDate.value, 1)
    fetchData()
}

function nextDay(): void {
    dayStep.value += 1
    currentDate.value = addDays(currentDate.value, 1)
    fetchData()
}

async function fetchData(): Promise<void> {
    try {
        const response = await dashboardStore.getDashboardItem({
            item: props.widget,
            paginate: '',
            query: `sent_date=${format(currentDate.value, 'yyyy-MM-dd')}`
        })
        const next = response?.data?.body?.content
        content.value = Array.isArray(next) ? (next as OrderLocationRow[]) : []
    } catch (error) {
        errorHandler(error)
    }
}

async function showDetails(item: OrderLocationRow): Promise<void> {
    partner.value = item.orderLocation || ''

    try {
        const response = await dashboardStore.getOrdersProductsPerDayNotSent({
            query: `dueDate=${format(currentDate.value, 'yyyy-MM-dd')}&partner=${partner.value}`
        })
        ordersProducts.value = (response?.data?.results || []) as NotSentDialogProductRow[]
        dialogOpen.value = true
    } catch (error) {
        errorHandler(error)
    }
}

async function generateReport(): Promise<void> {
    try {
        await dashboardStore.generateOrdersProductsPerDayNotSentReport({
            query: `dueDate=${format(currentDate.value, 'dd.MM.yyyy')}`,
            userEmail: userStore.userEmail || ''
        })
    } catch (error) {
        errorHandler(error)
    }
}

function closeDialog(): void {
    dialogOpen.value = false
}

// 9. Helpers / lifecycle
function onDialogAfterLeave(): void {
    ordersProducts.value = []
    partner.value = ''
}
</script>
